import * as React from 'react';
import { cloneDeep, get } from 'lodash-es';
import {
  Box,
  Button,
  FormField,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { Actions, composePaths } from '@jsonforms/core';
import { useJsonForms } from '@jsonforms/react';
import { useEntityData } from '@web-config-app/core-react';
import type { EntityFormStatePropsOfControlWithDetail } from '@web-config-app/entity-form';
import {
  CompoundPrimitiveGroupFieldset,
  createDefaultValueForSchema,
  findEnclosingItemPath,
  useFullEntityDataPath,
} from '@web-config-app/entity-form';
import { ConditionalActionEmptyState } from './conditional-action-empty-state.component';
import { ConditionalActionRuleCard } from './conditional-action-rule-card.component';
import { GoToPicker } from './go-to-picker.component';
import { focusById } from './focus-by-id';
import { useConditionalActionSchemaMeta } from './use-conditional-action-schema-meta/use-conditional-action-schema-meta';
import { goToAction, showAction, getGoToLinkId } from './types';
import type {
  ConditionalActionCondition,
  ConditionalActionData,
  ConditionalActionRule,
} from './types';

const cloneData = (data: ConditionalActionData): ConditionalActionData => ({
  // Spread first: unknown sibling keys survive the read-modify-write
  // cycle untouched. Only the keys this control owns are rebuilt.
  ...data,
  rules: (data.rules ?? []).map((rule) => ({
    ...rule,
    conditions: (rule.conditions ?? []).map((condition) => ({
      ...condition,
      // Deep-copy `inputs` (covers the `{values: string[]}` array case
      // for the `multiple` shape) so downstream mutation can't leak
      // into prior state. Inputs objects are tiny — cost is negligible.
      inputs: cloneDeep(condition.inputs),
    })),
  })),
});

const isEmpty = (
  data: ConditionalActionData | undefined,
  actionType: 'goTo' | 'show',
): boolean => {
  const noRules = (data?.rules?.length ?? 0) === 0;
  // `show` has no top-level default, so only rules decide emptiness — a
  // stray stored `default` must not suppress the empty state.
  return actionType === 'show' ? noRules : noRules && !data?.default;
};

/**
 * Coerce arbitrary stored JSONForms data into a well-formed
 * `ConditionalActionData`: wrong-typed `rules` becomes `[]`. Unknown sibling
 * keys are preserved (spread first) so a read-modify-write cycle never drops
 * fields this control doesn't own. Element shapes are re-validated at their
 * usage sites (operators, inputs).
 */
export const normalizeConditionalActionData = (
  data: unknown,
): ConditionalActionData => {
  if (!data || typeof data !== 'object') return {};
  const raw = data as Record<string, unknown>;
  return {
    ...raw,
    rules: Array.isArray(raw.rules)
      ? (raw.rules as ConditionalActionRule[])
      : [],
  } as ConditionalActionData;
};

export const ConditionalActionControl = ({
  data,
  path,
  schema,
  enabled,
}: EntityFormStatePropsOfControlWithDetail) => {
  const { formatMessage } = useIntl();
  const { dispatch } = useJsonForms();
  const entityData = useEntityData();
  // Full dot-notation path from entity-data root down to this control's
  // field — the base for child field ids and for locating the array item
  // the control is editing.
  const controlPath = useFullEntityDataPath(path);

  // The array item this control is rendered inside (e.g. the question).
  // Condition subjects read their value type and comparison-value options
  // off it via relative paths. Resolved once here and handed down.
  const enclosingItemData = React.useMemo(() => {
    const itemPath = findEnclosingItemPath(controlPath);
    return itemPath ? get(entityData, itemPath) : undefined;
  }, [entityData, controlPath]);

  const {
    titleKey,
    hintKey,
    emptyStateHintKey,
    emptyStateTitleKey,
    rulesArrayAddLabel,
    conditionSubjects,
    actionType,
    actionLabelKey,
    allowedOperators,
    allowMultipleConditions,
    operatorLabelOverrides,
    ruleItemSchema,
    destinationDataSource,
    destinationDataSourceType,
    destinationMapping,
    defaultLabelKey,
    defaultHintKey,
  } = useConditionalActionSchemaMeta(schema);

  // One warning per control mount (not per picker) when the schema
  // declares no destination data source — the pickers will render with
  // no options and authors can't set destinations.
  React.useEffect(() => {
    // Only the `goTo` config needs a destination data source; the `show`
    // config has no destination picker, so its absence is expected.
    if (actionType === 'goTo' && !destinationDataSource) {
      // eslint-disable-next-line no-console
      console.warn(
        'ConditionalActionControl: the goTo action config declares no `dataSource` ' +
          '— destination pickers will have no options.',
      );
    }
  }, [actionType, destinationDataSource]);

  const conditionalActionData = normalizeConditionalActionData(data);
  const rules: ConditionalActionRule[] = conditionalActionData.rules ?? [];
  // The destination linkId of the top-level default `goTo` action, if any
  // (absent / non-goTo / malformed → undefined; the picker shows unset).
  const defaultDestination = getGoToLinkId(conditionalActionData.default);

  // Stable per-rule React keys, held in a ref. Rules are anonymous in the
  // data model (no `id`), but keying cards by array index would remount
  // them on insert/delete and lose focus. These monotonic keys are UI-only
  // — never written back, so the stored/wire shape is untouched — and are
  // kept aligned with the rules array as it's added to and deleted from.
  const ruleKeysRef = React.useRef<string[]>([]);
  const ruleKeySeqRef = React.useRef(0);
  const mintRuleKey = () => {
    ruleKeySeqRef.current += 1;
    return `rule-${ruleKeySeqRef.current}`;
  };
  // Backfill on first render and whenever rules arrive/grow from stored
  // data. Deletes splice the matching key in `deleteRule`, so by the time
  // we render the lengths already match for the middle-delete case.
  while (ruleKeysRef.current.length < rules.length) {
    ruleKeysRef.current.push(mintRuleKey());
  }
  if (ruleKeysRef.current.length > rules.length) {
    ruleKeysRef.current.length = rules.length;
  }

  const update = (next: ConditionalActionData) => {
    dispatch?.(Actions.update(path, () => next));
  };

  const updateRule = (index: number, nextRule: ConditionalActionRule) => {
    const next = cloneData(conditionalActionData);
    next.rules = (next.rules ?? []).map((rule, i) =>
      i === index ? nextRule : rule,
    );
    update(next);
  };

  const addRule = () => {
    // With no subjects declared the schema is misconfigured — silently
    // no-op rather than creating an empty rule the author can't fill.
    // (Same policy as the rule card's `addCondition`.)
    const defaultSubject = conditionSubjects[0];
    if (!defaultSubject) return;
    // createDefaultValueForSchema is generic — typing it to ConditionalActionRule gives
    // a Partial<ConditionalActionRule> back, so the spread below needs no cast.
    const baseRule: Partial<ConditionalActionRule> =
      (ruleItemSchema &&
        createDefaultValueForSchema<ConditionalActionRule>(ruleItemSchema)) ||
      {};
    // Pre-fill the first declared subject on the first condition.
    // Operator and inputs stay empty — author picks the operator from
    // the dropdown next.
    const seedConditions: ConditionalActionCondition[] = [
      { subjectType: defaultSubject.type, operatorId: undefined, inputs: {} },
    ];
    const newRule: ConditionalActionRule = {
      ...baseRule,
      conditions: seedConditions,
      // The `show` config's action is fixed (no per-rule picker), so seed it
      // now; `goTo` rules leave `then` empty for the author to fill in.
      ...(actionType === 'show' ? { then: showAction() } : {}),
    };
    const next = cloneData(conditionalActionData);
    next.rules = [...(next.rules ?? []), newRule];
    update(next);
  };

  // Both Add-rule buttons (rule list + empty state) share this id —
  // they're never rendered simultaneously, and it gives the post-delete
  // focus handler one stable target either way.
  const addRuleButtonId = `${controlPath}.addRule`;

  const deleteRule = (index: number) => {
    const next = cloneData(conditionalActionData);
    next.rules = (next.rules ?? []).filter((_, i) => i !== index);
    // Drop the deleted rule's key so the remaining cards keep their keys
    // (and their identity) rather than shifting up by one.
    ruleKeysRef.current.splice(index, 1);
    update(next);
    // The delete button just unmounted with its card — keep keyboard
    // focus in the control rather than letting it fall to <body>.
    focusById(addRuleButtonId);
  };

  const updateDefault = (nextDest: string | undefined) => {
    const next = cloneData(conditionalActionData);
    // Wrap the chosen destination in a `goTo` action; clearing it removes
    // the default entirely (absent = linear next).
    next.default = nextDest ? goToAction(nextDest) : undefined;
    update(next);
  };

  return (
    <CompoundPrimitiveGroupFieldset
      label={formatMessage({ id: titleKey })}
      hint={hintKey ? formatMessage({ id: hintKey }) : undefined}
    >
      {isEmpty(conditionalActionData, actionType) ? (
        <ConditionalActionEmptyState
          titleKey={emptyStateTitleKey}
          emptyStateHintKey={emptyStateHintKey}
          addRuleLabelKey={rulesArrayAddLabel}
          addRuleButtonId={addRuleButtonId}
          onAddRule={addRule}
          enabled={enabled}
          actionType={actionType}
        />
      ) : (
        <Box
          css={{
            width: '100%',
            border: 'solid $colors$onSurfaceBorderSubdued $borderWidths$thin',
            borderRadius: '$medium',
            padding: '$one',
            backgroundColor: '$surfaceBackgroundPrimary',
          }}
        >
          <StackLayout orientation="vertical" spacing="$one">
            {rules.map((rule, index) => {
              // rulePath uses the full entity edit path so child field
              // ids/names are unique; the relative JSONForms `path` alone
              // is just the control's property name.
              const ruleKey = ruleKeysRef.current[index];
              return (
                <ConditionalActionRuleCard
                  key={ruleKey}
                  rule={rule}
                  ruleIndex={index}
                  rulePath={composePaths(controlPath, `rules.${index}`)}
                  conditionSubjects={conditionSubjects}
                  enclosingItemData={enclosingItemData}
                  actionType={actionType}
                  actionLabelKey={actionLabelKey}
                  allowedOperators={allowedOperators}
                  operatorLabelOverrides={operatorLabelOverrides}
                  destinationDataSource={destinationDataSource}
                  destinationDataSourceType={destinationDataSourceType}
                  destinationMapping={destinationMapping}
                  allowMultipleConditions={allowMultipleConditions}
                  enabled={enabled}
                  onChange={(next) => updateRule(index, next)}
                  onDelete={() => deleteRule(index)}
                />
              );
            })}
            {enabled && (
              <Button
                id={addRuleButtonId}
                icon="interfacePlus"
                size="small"
                priority="secondary"
                onClick={addRule}
                css={{ alignSelf: 'flex-start' }}
              >
                {formatMessage({
                  id: rulesArrayAddLabel ?? 'CONDITIONAL_ACTION_ADD_RULE_LABEL',
                })}
              </Button>
            )}
            {/* The default destination applies only to the `goTo` config.
                The `show` config's no-match behavior is an implicit hide —
                no default field. */}
            {actionType === 'goTo' && (
              <FormField
                id={`${path}.default`}
                label={formatMessage({ id: defaultLabelKey })}
                hint={formatMessage({ id: defaultHintKey })}
              >
                <GoToPicker
                  value={defaultDestination}
                  onChange={updateDefault}
                  placeholderKey="CONDITIONAL_ACTION_CHOOSE_DESTINATION"
                  enabled={enabled}
                  dataSourceName={destinationDataSource}
                  dataSourceType={destinationDataSourceType}
                  mapping={destinationMapping}
                />
              </FormField>
            )}
          </StackLayout>
        </Box>
      )}
    </CompoundPrimitiveGroupFieldset>
  );
};
